/**
 * Reviews Form JavaScript
 * Handles modal interactions and star rating functionality
 */

class ReviewsForm {
  constructor() {
    this.modal = document.querySelector('#reviews-form-modal');
    this.form = this.modal?.querySelector('form');
    this.backdrop = this.modal?.querySelector('.reviews-form-modal__backdrop');
    this.closeButton = this.modal?.querySelector('.reviews-form-modal__close');
    this.openButtons = document.querySelectorAll('#leave-review-btn, [data-action="open-review-form"]');
    
    this.init();
  }

  init() {
    if (!this.modal) return;

    // Bind event listeners
    this.bindModalEvents();
    this.bindStarRatingEvents();
    this.bindFormEvents();
  }

  bindModalEvents() {
    // Open modal
    this.openButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.openModal();
      });
    });

    // Close modal
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.closeModal());
    }

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.closeModal());
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isModalOpen()) {
        this.closeModal();
      }
    });
  }

  bindStarRatingEvents() {
    const starRatings = this.modal?.querySelectorAll('.star-rating--interactive');
    
    starRatings?.forEach(rating => {
      const stars = rating.querySelectorAll('.star-rating__star--clickable');
      const hiddenInput = rating.querySelector('.star-rating__input');
      let currentRating = parseInt(rating.dataset.rating) || 0;

      stars.forEach((star, index) => {
        const starValue = parseInt(star.dataset.value);

        // Click event
        star.addEventListener('click', (e) => {
          e.preventDefault();
          currentRating = starValue;
          this.updateStarDisplay(stars, currentRating);
          if (hiddenInput) {
            hiddenInput.value = currentRating;
          }
          rating.dataset.rating = currentRating;
        });

        // Hover events for preview
        star.addEventListener('mouseenter', () => {
          this.updateStarDisplay(stars, starValue, true);
        });

        star.addEventListener('mouseleave', () => {
          this.updateStarDisplay(stars, currentRating);
        });
      });
    });
  }

  updateStarDisplay(stars, rating, isPreview = false) {
    stars.forEach((star, index) => {
      const starValue = parseInt(star.dataset.value);
      const starSvg = star.querySelector('svg');
      
      if (starValue <= rating) {
        star.classList.add('star-rating__star--filled');
        star.classList.remove('star-rating__star--empty');
        if (starSvg) {
          starSvg.style.fill = 'currentColor';
        }
      } else {
        star.classList.add('star-rating__star--empty');
        star.classList.remove('star-rating__star--filled');
        if (starSvg) {
          starSvg.style.fill = 'transparent';
        }
      }

      if (isPreview) {
        star.style.opacity = starValue <= rating ? '1' : '0.3';
      } else {
        star.style.opacity = '1';
      }
    });
  }

  bindFormEvents() {
    if (!this.form) return;

    this.form.addEventListener('submit', (e) => {
      if (!this.validateForm()) {
        e.preventDefault();
        return false;
      }
    });

    // Real-time validation
    const requiredFields = this.form.querySelectorAll('[required]');
    requiredFields.forEach(field => {
      field.addEventListener('blur', () => this.validateField(field));
    });
  }

  validateForm() {
    let isValid = true;
    const requiredFields = this.form.querySelectorAll('[required]');
    
    requiredFields.forEach(field => {
      if (!this.validateField(field)) {
        isValid = false;
      }
    });

    // Special validation for star rating
    const starRating = this.form.querySelector('.star-rating--interactive');
    if (starRating) {
      const rating = parseInt(starRating.dataset.rating) || 0;
      if (rating === 0) {
        this.showFieldError(starRating, 'Please select a rating');
        isValid = false;
      } else {
        this.clearFieldError(starRating);
      }
    }

    return isValid;
  }

  validateField(field) {
    const value = field.value.trim();
    let isValid = true;

    // Clear previous errors
    this.clearFieldError(field);

    if (field.hasAttribute('required') && !value) {
      this.showFieldError(field, 'This field is required');
      isValid = false;
    }

    if (field.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        this.showFieldError(field, 'Please enter a valid email address');
        isValid = false;
      }
    }

    return isValid;
  }

  showFieldError(field, message) {
    field.classList.add('field--error');
    field.setAttribute('aria-invalid', 'true');
    
    // Remove existing error message
    this.clearFieldError(field);
    
    // Create new error message
    const errorElement = document.createElement('small');
    errorElement.className = 'field__error';
    errorElement.textContent = message;
    
    const fieldContainer = field.closest('.field') || field.parentElement;
    fieldContainer.appendChild(errorElement);
  }

  clearFieldError(field) {
    field.classList.remove('field--error');
    field.removeAttribute('aria-invalid');
    
    const fieldContainer = field.closest('.field') || field.parentElement;
    const existingError = fieldContainer.querySelector('.field__error');
    if (existingError) {
      existingError.remove();
    }
  }

  openModal() {
    if (!this.modal) return;
    
    this.modal.style.display = 'flex';
    document.body.classList.add('reviews-modal-open');
    
    // Focus the first form field
    const firstField = this.form?.querySelector('input, textarea');
    if (firstField) {
      setTimeout(() => firstField.focus(), 100);
    }

    // Announce to screen readers
    this.modal.setAttribute('aria-hidden', 'false');
  }

  closeModal() {
    if (!this.modal) return;
    
    this.modal.style.display = 'none';
    document.body.classList.remove('reviews-modal-open');
    this.modal.setAttribute('aria-hidden', 'true');

    // Reset form if not submitted successfully
    if (!this.form?.querySelector('.form-status-list')) {
      this.resetForm();
    }
  }

  resetForm() {
    if (!this.form) return;

    this.form.reset();
    
    // Reset star rating
    const starRating = this.form.querySelector('.star-rating--interactive');
    if (starRating) {
      starRating.dataset.rating = '0';
      const hiddenInput = starRating.querySelector('.star-rating__input');
      if (hiddenInput) {
        hiddenInput.value = '0';
      }
      
      const stars = starRating.querySelectorAll('.star-rating__star');
      this.updateStarDisplay(stars, 0);
    }

    // Clear all errors
    const errorFields = this.form.querySelectorAll('.field--error');
    errorFields.forEach(field => this.clearFieldError(field));
  }

  isModalOpen() {
    return this.modal && this.modal.style.display !== 'none';
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new ReviewsForm();
});

// Handle theme editor
document.addEventListener('shopify:section:load', () => {
  new ReviewsForm();
});